import { useCallback, useRef, useState } from 'react';
import * as S from './styles';
import { FaCamera } from 'react-icons/fa';
import { FiX } from 'react-icons/fi';
import { useImageUploader } from './useImageUploader';
import { CustomButton } from '../CustomButton';
import Dropzone from 'react-dropzone';
import { useCustomTheme } from '../../hooks/useCustomTheme';
import RModal from '../Rsuite/Modal';
import ReactCrop from 'react-image-crop'
import { toastError } from '../../toasts';

const styles = {
  icon: {
    width: '100%',
    height: '100%',
  },
};

const zoomOpcoes = {
  maximo: 3,
  minimo: -1,
  incremento: 0.1,
};

export const ImageUploader = ({
  size = 100,
  width,
  height,
  src,
  save,
  remove,
  type = 'logo' | 'background',
}) => {
  const {
    preview,
    open,
    handleSelectImage,
    handleUnSelectImage,
    handleOpenSelect,
    handleOpenModal,
    onClose
  } = useImageUploader({ save, remove });
  const [crop, setCrop] = useState({
    x: 0,
    y: 0,
    ...(type === 'logo'
      ? { width: '100%', height: 180 }
      : { width: '100%', height: 110 })
  });
  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  const rotate = 0;
  const [completedCrop, setCompletedCrop] = useState(null)
  const [zoom, setZoom] = useState(1);
  const croppedArea = useRef(null);
  const { theme } = useCustomTheme();

  async function onDrop(acceptedFiles) {
    const file = acceptedFiles[0];
    handleSelectImage({ target: { files: [file] } });
  }

  function cancel() {
    handleUnSelectImage()
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    croppedArea.current = null;
    onClose();
  }

  const onSubmit = useCallback(() => {
    if (!completedCrop || !imgRef.current || !canvasRef.current) {
      toastError('Selecione uma imagem.');
      return;
    };

    const image = imgRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    const pixelRatio = window.devicePixelRatio;
    canvas.width = completedCrop.width * pixelRatio;
    canvas.height = completedCrop.height * pixelRatio;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(
      image,
      completedCrop.x,
      completedCrop.y,
      completedCrop.width,
      completedCrop.height,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], "cropped-image.jpg", { type: "image/jpeg" });
      save(file);
      onClose();
    }, "image/jpeg");

  },[completedCrop, onClose, save]);

  return (
    <>
      <S.Container size={size} width={width} height={height} onClick={handleOpenModal}>
        {(src || preview) && (
          <S.IconUnSelectWrapper onClick={handleUnSelectImage}>
            <FiX size={20} color="#ffffff" />
          </S.IconUnSelectWrapper>
        )}
        <S.Content onClick={handleOpenSelect}>
          {(!!src || !!preview) && <S.Image src={src ?? preview} />}

          {!src && !preview && (
            <S.IconWrapper>
              <FaCamera style={styles.icon} color="#777777" />
            </S.IconWrapper>
          )}
        </S.Content>
      </S.Container>
      {open &&
        <RModal
          open={open}
          onClose={onClose}
          title={`Selecione a imagem`}
          footer={
            <div className="imagemControls" style={{ marginTop: 20 }}>
              <CustomButton appearance="default" onClick={cancel}>
                Cancelar
              </CustomButton>
              <CustomButton
                appearance="ghost"
                disabled={zoom === zoomOpcoes.minimo}
                onClick={() =>
                  setZoom(Math.max(zoom - zoomOpcoes.incremento, zoomOpcoes.minimo))
                }
              >
                Zoom -
              </CustomButton>
              <CustomButton
                appearance="ghost"
                disabled={zoom === zoomOpcoes.maximo}
                onClick={() =>
                  setZoom(Math.min(zoom + zoomOpcoes.incremento, zoomOpcoes.maximo))
                }
              >
                Zoom +
              </CustomButton>
              <CustomButton appearance="primary" onClick={onSubmit}>
                Salvar
              </CustomButton>
            </div>
          }>
          <div
            className="modal-imagem-grupo-container"
            style={{ background: theme.colors.background }}
          >
            <div
              className="imagemContainer"
              style={{ background: theme.colors.background }}
            >
              {preview ? (
                  <ReactCrop
                    crop={crop}
                    onChange={c => setCrop(c)}
                    style={{ border: '3px solid #cbcbc' }}
                    onComplete={(c) => setCompletedCrop(c)}
                    onImageLoaded={(img) => (imgRef.current = img)}
                    >
                    <img
                      ref={imgRef}
                      src={preview}
                      alt={type}
                      style={{ transform: `scale(${zoom}) rotate(${rotate}deg)` }}
                    />
                  </ReactCrop>
              ) : (
                <Dropzone
                  maxFiles={1}
                  accept="image/*"
                  onDropAccepted={onDrop}
                >
                  {({
                    getRootProps,
                    getInputProps,
                    isDragAccept,
                    isDragReject,
                  }) => (
                    <div
                      {...getRootProps({ className: 'dropzone' })}
                      className={`dropContainer ${
                        isDragAccept ? 'dragAccepted' : ''
                      } ${isDragReject ? 'dragReject' : ''}`}
                    >
                      <input {...getInputProps()} />
                      <p className={'content'} style={{ color: theme.colors.text }}>
                        {isDragReject
                          ? 'Arquivo selecionado não é uma imagem válida'
                          : 'Arraste uma imagem ou clique para selecionar'}
                      </p>
                    </div>
                  )}
                </Dropzone>
              )}
            </div>
            <canvas ref={canvasRef} style={{ display: "none" }} />
        </div>
      </RModal>}
    </>
  );
};
